import React, { useState, useEffect, useRef } from "react";
import { FaShoppingCart, FaExchangeAlt, FaDraftingCompass, FaHardHat, FaFileInvoiceDollar, FaTruck, FaCubes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import "./ContainerGrid.css";
import "./TrocarProjetoModal.css";

const getConsistentColor = (str) => {
  if (!str) return "#81C784";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 65%, 60%)`;
};

const SETOR_VISUAL = [
  { match: /compr/i, icon: FaShoppingCart, bg: "#dcfce7", color: "#16a34a" },
  { match: /or[çc]amento|planej/i, icon: FaExchangeAlt, bg: "#dbeafe", color: "#2563eb" },
  { match: /arquitet/i, icon: FaDraftingCompass, bg: "#ede9fe", color: "#7c3aed" },
  { match: /obra/i, icon: FaHardHat, bg: "#fef3c7", color: "#d97706" },
  { match: /financ/i, icon: FaFileInvoiceDollar, bg: "#dcfce7", color: "#16a34a" },
  { match: /suprim/i, icon: FaTruck, bg: "#fee2e2", color: "#dc2626" },
];
const getSetorVisual = (nome) => SETOR_VISUAL.find(v => v.match.test(nome || "")) || { icon: FaCubes, bg: "#f1f5f9", color: "#475569" };

function ScrollStrip({ children }) {
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    checkScroll();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [children]);

  const scrollBy = (dir) => trackRef.current?.scrollBy({ left: dir * 200, behavior: "smooth" });

  return (
    <div className="tp-strip-wrap">
      {canLeft && (
        <button className="tp-arrow tp-arrow--left" onClick={() => scrollBy(-1)}>
          <FaChevronLeft size={10} />
        </button>
      )}
      <div className="tp-strip" ref={trackRef}>
        {children}
      </div>
      {canRight && (
        <button className="tp-arrow tp-arrow--right" onClick={() => scrollBy(1)}>
          <FaChevronRight size={10} />
        </button>
      )}
    </div>
  );
}

export default function TrocarProjetoModal({ containerId, currentUserId, currentEntityId, onClose }) {
  const [projetos, setProjetos] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAberto(true), 10);
    return () => clearTimeout(t);
  }, []);

  const fecharComAnimacao = () => {
    setAberto(false);
    setTimeout(onClose, 220);
  };

  useEffect(() => {
    const carregar = async () => {
      if (!containerId || !currentUserId) { setLoading(false); return; }
      try {
        const { data: projectsRaw, error: projErr } = await supabase
          .from("projects").select("*").eq("user_id", containerId);
        if (projErr) console.error("Erro ao buscar projetos:", projErr);

        const { data: setoresRaw, error: setErr } = await supabase
          .from("setores").select("*").eq("user_id", containerId);
        if (setErr) console.error("Erro ao buscar setores:", setErr);

        const projetosComFoto = await Promise.all(
          (projectsRaw || []).map(async (p) => {
            if (p.photo_url) return p;
            const { data: photoData } = await supabase
              .from("projects_photos").select("photo_url").eq("project_id", p.id)
              .order("created_at", { ascending: false }).limit(1).maybeSingle();
            return { ...p, photo_url: photoData?.photo_url || null };
          })
        );

        const setoresComFoto = await Promise.all(
          (setoresRaw || []).map(async (s) => {
            if (s.photo_url) return s;
            const { data: photoData } = await supabase
              .from("setores_photos").select("photo_url").eq("setor_id", s.id)
              .order("created_at", { ascending: false }).limit(1).maybeSingle();
            return { ...s, photo_url: photoData?.photo_url || null };
          })
        );

        if (currentUserId === containerId) {
          setProjetos(projetosComFoto);
          setSetores(setoresComFoto);
        } else {
          const { data: permissoes } = await supabase
            .from("permissoes_colaboradores")
            .select("projeto_id, setor_id")
            .eq("colaborador_id", currentUserId)
            .eq("container_id", containerId);
          const projPerm = (permissoes || []).filter(p => p.projeto_id).map(p => p.projeto_id);
          const setPerm = (permissoes || []).filter(p => p.setor_id).map(p => p.setor_id);
          setProjetos(projetosComFoto.filter(p => projPerm.includes(p.id)));
          setSetores(setoresComFoto.filter(s => setPerm.includes(s.id)));
        }
      } catch (err) {
        console.error("Erro ao carregar projetos/setores:", err);
      } finally { setLoading(false); }
    };
    carregar();
  }, [containerId, currentUserId]);

  const irPara = (id, type) => {
    if (String(id) === String(currentEntityId)) { fecharComAnimacao(); return; }
    const params = new URLSearchParams();
    params.set("entityId", id);
    params.set("type", type);
    params.set("containerId", containerId);
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  };

  return (
    <div className={`tp-bar${aberto ? " tp-bar--open" : ""}`}>
      <div className="tp-bar__inner">
        <button className="tp-close" onClick={fecharComAnimacao}>✕</button>

        {loading ? (
          <p className="tp-loading">Carregando...</p>
        ) : (projetos.length === 0 && setores.length === 0) ? (
          <p className="tp-empty">Nenhum resultado.</p>
        ) : (
          <div className="tp-rows">
            {projetos.length > 0 && (
              <div className="tp-row">
                <span className="tp-row-label">Projetos</span>
                <ScrollStrip>
                  {projetos.map(proj => {
                        const ativo = String(proj.id) === String(currentEntityId);
                        return (
                            <div
                            key={proj.id}
                            className={`cx-card tp-mini${ativo ? " tp-mini--active" : ""}`}
                            onClick={() => irPara(proj.id, "project")}
                            >
                            <div
                                className="cx-card__cover"
                                style={{
                                backgroundImage: proj.photo_url ? `url(${proj.photo_url})` : "none",
                                backgroundColor: proj.photo_url ? undefined : getConsistentColor(proj.id),
                                }}
                            >
                                {!proj.photo_url && <span className="cx-card__initial">{proj.name?.charAt(0)?.toUpperCase() || "?"}</span>}
                            </div>
                            <div className="cx-card__divider" />
                            <div className="cx-card__body"><h3>{proj.name || "Projeto"}</h3></div>
                            </div>
                        );
                        })}
                </ScrollStrip>
              </div>
            )}

            {setores.length > 0 && (
              <div className="tp-row">
                <span className="tp-row-label">Setores</span>
                <ScrollStrip>
                  {setores.map(setor => {
                    const { icon: Icon, bg, color } = getSetorVisual(setor.name);
                    const ativo = String(setor.id) === String(currentEntityId);
                    return (
                      <div
                        key={setor.id}
                        className={`cx-setor-card tp-mini${ativo ? " tp-mini--active" : ""}`}
                        onClick={() => irPara(setor.id, "setor")}
                      >
                        <div className="cx-setor-card__content">
                          <div className="cx-setor-card__icon" style={{ background: bg, color }}>
                            {setor.photo_url ? <img src={setor.photo_url} alt={setor.name} /> : <Icon size={11} />}
                          </div>
                          <div className="cx-setor-card__text"><h3>{setor.name || "Setor"}</h3></div>
                        </div>
                      </div>
                    );
                  })}
                </ScrollStrip>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}